import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { analytics, useAnalytics } from '@/lib/analytics';
import { useAuthOptional } from './AuthProvider';

interface AnalyticsContextValue {
  analytics: typeof analytics;
  trackEvent: (action: string, category: string, label?: string, value?: number) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
  measurementId?: string;
  gtmId?: string;
}

export function AnalyticsProvider({ children, measurementId, gtmId }: AnalyticsProviderProps) {
  const auth = useAuthOptional();
  const user = auth?.user ?? null;
  const organization = auth?.organization ?? null;
  const location = useLocation();
  const analyticsInstance = useAnalytics();
  const pageStartTime = useRef<number>(Date.now());
  const scrollDepthTracked = useRef<Set<number>>(new Set());

  // Initialize Google Analytics and GTM
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Load Google Analytics if measurement ID is provided
    if (measurementId && !window.gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };

      window.gtag('js', new Date().toISOString());
      window.gtag('config', measurementId, {
        anonymize_ip: true,
        allow_ad_features: false,
      });
    }

    // Load Google Tag Manager if GTM ID is provided
    if (gtmId && !document.querySelector(`script[src*="${gtmId}"]`)) {
      const gtmScript = document.createElement('script');
      gtmScript.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');
      `;
      document.head.appendChild(gtmScript);

      // Add GTM noscript fallback
      const noscript = document.createElement('noscript');
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);
    }
  }, [measurementId, gtmId]);

  // Track page views on route changes
  useEffect(() => {
    const currentTime = Date.now();
    const timeSpent = pageStartTime.current ? currentTime - pageStartTime.current : 0;

    // Track time spent on previous page
    if (timeSpent > 5000) { // Only track if spent more than 5 seconds
      analytics.trackTimeOnPage(location.pathname, Math.round(timeSpent / 1000));
    }

    // Track new page view
    analytics.trackPageView(location.pathname, document.title);

    // Reset page start time and scroll tracking
    pageStartTime.current = currentTime;
    scrollDepthTracked.current.clear();

    // Set up scroll depth tracking for this page
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      // Track at 25%, 50%, 75%, 90% scroll depths
      const thresholds = [25, 50, 75, 90];
      thresholds.forEach(threshold => {
        if (scrollPercent >= threshold && !scrollDepthTracked.current.has(threshold)) {
          analytics.trackScrollDepth(location.pathname, threshold);
          scrollDepthTracked.current.add(threshold);
        }
      });
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  // Update user properties when user data changes
  useEffect(() => {
    if (user && organization) {
      analytics.setUserProperties({
        user_id: user.id,
        user_type: user.emailVerified ? 'verified' : 'unverified',
        organization_id: organization.id,
        plan_type: 'unknown', // This would be populated from subscription data
        signup_date: (user as { createdAt?: string }).createdAt ? new Date((user as { createdAt?: string }).createdAt!).toISOString().split('T')[0] : undefined,
        onboarding_completed: false, // This would be tracked separately
      });
    }
  }, [user, organization]);

  // Track page visibility changes (user switching tabs)
  useEffect(() => {
    let hiddenStartTime: number | null = null;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenStartTime = Date.now();
      } else if (hiddenStartTime) {
        const hiddenTime = Date.now() - hiddenStartTime;
        if (hiddenTime > 30000) { // Only track if hidden for more than 30 seconds
          analytics.trackEvent({
            action: 'tab_focus_return',
            category: 'engagement',
            customParameters: {
              time_hidden_seconds: Math.round(hiddenTime / 1000),
              page_path: location.pathname,
            },
          });
        }
        hiddenStartTime = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location.pathname]);

  const contextValue: AnalyticsContextValue = {
    analytics: analyticsInstance,
    trackEvent: (action: string, category: string, label?: string, value?: number) => {
      analytics.trackEvent({
        action,
        category,
        label,
        value,
      });
    },
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalyticsContext() {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider');
  }
  return context;
}